import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  companyPaymentLines,
  getCompanyPaymentDetails,
  saveCompanyPaymentDetails,
  uploadCompanyPaymentQr,
} from "../../../services/companyPaymentApi";

const FIELDS = [
  { key: "accountHolder", label: "Account Holder", placeholder: "INDIAN ODYSSEY TRIPS" },
  { key: "accountNumber", label: "Account Number", placeholder: "50200116232553" },
  { key: "ifsc", label: "IFSC", placeholder: "HDFC0008574" },
  { key: "branch", label: "Branch", placeholder: "ATCHAMPET JUNCTION" },
  { key: "accountType", label: "Account Type", placeholder: "Current Account" },
  { key: "upiNumber", label: "UPI Number", placeholder: "9121943442" },
  { key: "upiId", label: "UPI ID (optional)", placeholder: "yourname@hdfcbank" },
  { key: "upiName", label: "Name shown on UPI", placeholder: "Indian Odyssey Trips" },
];

const blank = FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: "" }), { qrImageUrl: "", qrS3Key: "" });

export default function CompanyPaymentDetailsManager() {
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCompanyPaymentDetails();
      setForm({ ...blank, ...Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v ?? ""])) });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load payment details");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => setForm((old) => ({ ...old, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveCompanyPaymentDetails(form);
      setForm({ ...blank, ...Object.fromEntries(Object.entries(saved).map(([k, v]) => [k, v ?? ""])) });
      toast.success("Payment details saved");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save payment details");
    } finally {
      setSaving(false);
    }
  };

  const onPickQr = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { publicUrl, s3Key } = await uploadCompanyPaymentQr(file);
      // Saved immediately: an uploaded file that is not persisted is just an orphan in the bucket.
      const saved = await saveCompanyPaymentDetails({ ...form, qrImageUrl: publicUrl, qrS3Key: s3Key });
      setForm({ ...blank, ...Object.fromEntries(Object.entries(saved).map(([k, v]) => [k, v ?? ""])) });
      toast.success("QR code uploaded");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not upload the QR code");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeQr = async () => {
    setUploading(true);
    try {
      const saved = await saveCompanyPaymentDetails({ ...form, qrImageUrl: "", qrS3Key: "" });
      setForm({ ...blank, ...Object.fromEntries(Object.entries(saved).map(([k, v]) => [k, v ?? ""])) });
      toast.success("QR code removed");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not remove the QR code");
    } finally {
      setUploading(false);
    }
  };

  const previewLines = companyPaymentLines(form);

  if (loading) {
    return (
      <Card className="shadow-sm">
        <Card.Body className="text-center py-5"><Spinner /></Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">Company Payment Details</div>
          <div className="small text-muted">
            Used in the payment reminder customers receive. Change it here and every reminder follows.
          </div>
        </div>
        <Button variant="primary" onClick={save} disabled={saving || uploading}>
          {saving ? <><Spinner size="sm" className="me-2" />Saving</> : "Save"}
        </Button>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          {FIELDS.map((field) => (
            <Col md={6} lg={4} key={field.key}>
              <Form.Label>{field.label}</Form.Label>
              <Form.Control
                value={form[field.key] || ""}
                placeholder={field.placeholder}
                disabled={saving}
                onChange={(event) => update(field.key, event.target.value)}
              />
            </Col>
          ))}
        </Row>

        <hr className="my-4" />

        <Row className="g-4">
          <Col md={6}>
            <div className="fw-semibold mb-1">Payment QR Code</div>
            <div className="small text-muted mb-2">
              The image staff copy alongside the reminder. PNG copies to the clipboard most reliably.
            </div>
            <Form.Control
              ref={fileRef}
              type="file"
              accept="image/*"
              disabled={uploading || saving}
              onChange={onPickQr}
            />
            {uploading && <div className="small text-muted mt-2"><Spinner size="sm" className="me-2" />Uploading…</div>}
            {form.qrImageUrl && (
              <div className="mt-3">
                <img
                  src={form.qrImageUrl}
                  alt="Payment QR code"
                  className="border rounded"
                  style={{ maxWidth: 220, maxHeight: 220, objectFit: "contain", background: "#fff" }}
                />
                <div className="mt-2 d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" href={form.qrImageUrl} target="_blank" rel="noreferrer">
                    Open
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={removeQr} disabled={uploading || saving}>
                    Remove
                  </Button>
                </div>
              </div>
            )}
          </Col>

          <Col md={6}>
            <div className="fw-semibold mb-1">How it appears in the reminder</div>
            <div className="small text-muted mb-2">
              Blank fields are left out, so a partly filled record still reads properly.
            </div>
            <pre className="small bg-light border rounded p-3 mb-0" style={{ whiteSpace: "pre-wrap" }}>
              {previewLines.length
                ? `Payment Details\n${previewLines.join("\n")}`
                : "Nothing filled in yet - the reminder will leave the payment section out."}
            </pre>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
